// API client for POST /api/sync/:repo (RFC-0002 Task 3.3).
//
// Repo convention: plain same-origin `fetch` (the SPA is served from the API's
// own origin — no CORS, no axios). This is a one-shot mutation client and stays
// on plain `fetch`; the SPA's *read* data layer wraps `fetch` with TanStack
// Query per ADR-0007 (`fetch` remains the transport). Non-2xx responses are
// mapped to typed error classes so the modal can decide inline vs. banner
// presentation.

import type { ConnectRepoResult } from '@portal/shared-types';

/** Shape of the shared G13 error envelope returned by the API. */
interface G13ErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * A 400 `invalid_repo_name` from the server. Shown inline near the input — the
 * name the user typed is not acceptable.
 */
export class RepoNameError extends Error {
  readonly code = 'invalid_repo_name';
  constructor(message: string) {
    super(message);
    this.name = 'RepoNameError';
  }
}

/**
 * A network failure or a 5xx from the server. Shown as a dismissible banner
 * with a retry action — the request itself failed, not the input.
 */
export class SyncRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SyncRequestError';
  }
}

/**
 * Queue a sync for `repo` within the configured org. Resolves with the
 * ConnectRepoResult on 202; rejects with RepoNameError (400) or
 * SyncRequestError (network / 5xx / unexpected).
 */
export async function connectRepo(repo: string): Promise<ConnectRepoResult> {
  let res: Response;
  try {
    res = await fetch(`/api/sync/${encodeURIComponent(repo)}`, {
      method: 'POST',
    });
  } catch {
    throw new SyncRequestError(
      'Could not reach the server. Check your connection and try again.',
    );
  }

  if (res.status === 202) {
    return (await res.json()) as ConnectRepoResult;
  }

  // Try to read the G13 envelope for a useful message.
  let body: G13ErrorBody | null = null;
  try {
    body = (await res.json()) as G13ErrorBody;
  } catch {
    body = null;
  }
  const message = body?.error?.message;

  if (res.status === 400 && body?.error?.code === 'invalid_repo_name') {
    throw new RepoNameError(message ?? 'That repository name is not valid.');
  }

  throw new SyncRequestError(
    message ?? 'Something went wrong queuing the sync. Please try again.',
  );
}
